import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from '@nextui-org/react';
import { apiFetch, apiTry, apiUpload } from '../lib/api';
import { auth } from '../lib/firebase';
import { maskEmail } from '../lib/mask';
import { ALL_LANGS, APP_FONT_URL, EDIT_LANG_LIST as LANGS, HomeShowcase, OTHER_LANGS, fitImage } from '../lib/homeShowcase';
import { SAVE_SECTIONS, changedByOthers, discardSection, freshDoc, isDirty, isStale, mergeFresh } from '../lib/showcaseSync';
import TranslateDialog from './TranslateDialog';
import { CATALOG } from '../data/widgets-catalog';
import { thumbFor } from '../data/widgetThumbs';

// Inicio de la app → los carruseles de «Widgets», «Animaciones LIVE» y
// «Gaming». Aquí se elige qué sale en cada uno y en qué orden, con qué imagen
// y con qué texto debajo. A la izquierda el formulario; a la derecha, el
// carrusel tal y como lo pinta la app (mismas clases y misma hoja de estilos:
// lib/homeShowcase.js), también al pasar el ratón por una tarjeta.
//
// Los elementos se eligen VIENDO su imagen (un selector con las miniaturas, un
// buscador y, en las animaciones, filtros por animación y por pack): solo con
// el nombre, en un desplegable, no se sabía qué se estaba eligiendo.
//
// En Gaming, cada juego puede llevar hasta tres vídeos de TikTok: con alguno,
// pulsar su tarjeta en la app abre un modal con sus portadas y «Ir al juego»
// (debajo de la vista previa se ve ese modal); sin ninguno, va directa a su
// ficha. Se pega el enlace y las visualizaciones; la portada la pone el Worker
// al guardar.
//
// Un carrusel sin elementos no sale vacío: la app lo rellena sola con los más
// usados. Un campo de texto vacío = el nombre que ya tiene en la app (en el
// idioma de cada usuario).
//
// Idiomas: el nombre y la frase se escriben a mano en español e inglés; los
// otros doce de la app se rellenan con «Traducir con ChatGPT», igual que en
// los banners y popups (se copia un prompt, se pega el JSON de vuelta). Lo que
// no esté traducido se ve en inglés. Las traducciones se pueden ver y corregir
// en cada elemento («Otros idiomas») y comprobar con el idioma de la vista previa.
// Al guardar, las apps abiertas lo enseñan en un minuto, más o menos (el feed
// se guarda un minuto en el edge y la app lo revalida cada minuto).
//
// Cuarta pestaña, «Galería · Destacados»: los tres elementos (widgets, animaciones o apartados
// de Metas) que salen arriba de la Galería de overlays de la app, en «Todo». Allí solo se ve su
// imagen y su nombre, y un clic lleva a su tarjeta. Cada elemento tiene UNA imagen y UN nombre:
// si un destacado está también en su carrusel del Inicio, son los de allí (se cambian allí y
// cambian en los dos sitios); si no, se ponen aquí. Va en la misma fila de D1 (`gallery`).
// Worker: cf-worker/src/api/home.js (D1 app_config['home_showcase']); modelo
// compartido con la app: renderer/tabs/inicio/componentes/destacados-modelo.js.
//
// CADA PESTAÑA SE GUARDA POR SEPARADO (Fran, 14-oct-2026): «Guardar» manda solo la pestaña
// abierta y el Worker la pone encima de lo guardado, sin tocar las demás. Antes se mandaban las
// cuatro con lo que tenía la página al abrirse, y guardar los destacados borró los carruseles que
// otro admin había guardado mientras tanto. Lo que guardan los demás aparece solo: la página lo
// vuelve a pedir cada 30 s y al volver a ella (una pestaña sin cambios toma lo guardado; una con
// cambios sin guardar se queda como está). Si alguien guarda la misma pestaña mientras la editas,
// se avisa y se elige: ver lo suyo o guardar lo tuyo encima (el Worker no la pisa sin preguntar).

const ENDPOINT = '/api/backoffice/home/showcase';
const REFRESH_MS = 30 * 1000;
const M = HomeShowcase;
const GAMES_BASE = 'https://storage.tikcontrol.live/games';
const NO_PACKS = new Set(['random', 'custom']);
const COMING = new Set(['coming-soon', 'coming_soon']);

// En el orden en que salen en el Inicio de la app.
const SECTIONS = [
  {
    id: 'games', label: 'Gaming', title: 'Gaming', sub: 'Convierte los regalos en parte del juego.',
    all: 'Ver todos los juegos', action: 'Jugar', shape: '16:9', hint: 'El nombre se ve siempre; la frase y el botón, al pasar el ratón.'
  },
  {
    id: 'widgets', label: 'Widgets', title: 'Widgets', sub: 'Herramientas interactivas para transformar tus LIVE.',
    all: 'Ver todos los widgets', action: 'Añadir', shape: '16:9', hint: 'El nombre se ve siempre; la frase y el botón, al pasar el ratón.'
  },
  {
    id: 'animations', label: 'Animaciones LIVE', title: 'Animaciones LIVE', sub: 'Packs de animaciones para hacer de cada regalo un momento inolvidable.',
    all: 'Ver todas las animaciones', action: 'Añadir', shape: '9:16', hint: 'El nombre, la frase y el botón salen al pasar el ratón (y la animación se reproduce).'
  }
];

const TEXT_FIELDS = [['title', 'Nombre'], ['text', 'Frase']];

// Destacados de la Galería de overlays.
const GALLERY_TAB = 'gallery';
const KIND_LABEL = { widgets: 'Widget', animations: 'Animación', goals: 'Meta' };
const KIND_HOME = { widgets: 'Widgets', animations: 'Animaciones LIVE' };
// Los apartados de Metas de la galería (las subpestañas de su módulo en la app).
const GOAL_OPTIONS = [
  { key: 'classic', name: 'Metas', sub: 'Monedas, likes, seguidores…', thumb: '', full: false, cover: false },
  { key: 'countdown', name: 'Metas decrecientes', sub: 'Cuenta atrás', thumb: '', full: false, cover: false },
  { key: 'gift', name: 'Metas de regalo', sub: 'Un regalo concreto', thumb: '', full: false, cover: false }
];
const blankFeatured = (kind, key) => ({ kind, key, image: '', title: { es: '', en: '' } });
// ¿Está ese destacado también en su carrusel del Inicio? (entonces su imagen y su nombre son los de allí)
const homeTwinIndex = (form, entry) => (entry.kind === 'goals' ? -1 : (form[entry.kind] || []).findIndex((item) => norm(item.key) === norm(entry.key)));
const MINI_FIELD = 'h-8 w-full min-w-0 rounded-md border border-default-200 bg-default-100 px-2 text-xs text-foreground outline-none transition-colors focus:border-primary disabled:opacity-40';

const blankItem = (key) => ({ key, image: '', title: { es: '', en: '' }, text: { es: '', en: '' } });
const norm = (key) => String(key || '').toLowerCase().replace(/_/g, '-');

// Para buscar: sin mayúsculas ni acentos («quiereme» encuentra «Quiéreme»).
const fold = (text) => String(text || '').toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
const uniq = (list) => [...new Set(list.filter(Boolean))];

// ── Guardado por pestañas (lib/showcaseSync.js) ─────────────────
const SECTION_LABEL = { ...Object.fromEntries(SECTIONS.map((s) => [s.id, s.label])), [GALLERY_TAB]: 'Galería · Destacados' };

// ── Traducciones (como en los banners: un prompt para ChatGPT) ───
const fieldId = (section, key, field) => `${section}|${key}|${field}`;

// Los textos que se mandan a traducir: los que tienen español y les falta
// algún idioma, más los que se han cambiado a mano después de traducirlos
// (`stale`); con `all`, todos los que tienen español. Cada uno con una clave
// corta para el prompt («w1_title»): ChatGPT las devuelve tal cual. Con `only`, solo los de esa
// pestaña (el editor traduce y guarda cada pestaña por separado); sin él, todos.
function translationFields(form, { all, stale, only }) {
  const out = [];
  M.SECTIONS.filter((section) => !only || section === only).forEach((section) => form[section].forEach((item, index) => TEXT_FIELDS.forEach(([field]) => {
    const es = String(item[field].es || '').trim();
    if (!es) return;
    if (!all && !stale.has(fieldId(section, item.key, field)) && !M.missingLangs(item[field]).length) return;
    // La clave: la inicial del carrusel, el puesto del elemento y el campo.
    out.push({ id: `${section[0]}${index + 1}_${field}`, section, key: item.key, field, es });
  })));
  // El nombre propio de un destacado de la galería (el que no lo toma de su carrusel del Inicio).
  (!only || only === GALLERY_TAB ? form.gallery || [] : []).forEach((item, index) => {
    const es = String(item.title.es || '').trim();
    if (!es || homeTwinIndex(form, item) !== -1) return;
    if (!all && !stale.has(fieldId(GALLERY_TAB, item.key, 'title')) && !M.missingLangs(item.title).length) return;
    out.push({ id: `d${index + 1}_title`, section: GALLERY_TAB, key: item.key, field: 'title', es });
  });
  return out;
}

function translationPrompt(fields) {
  const targets = ALL_LANGS.filter((l) => l.id !== 'es').map((l) => ({ code: l.id, name: l.name }));
  return [
    'Traduce estos textos de la pantalla de inicio de TikControl (una app para creadores de TikTok LIVE) desde español al resto de idiomas.',
    'Son los nombres y las frases que van debajo de las tarjetas de widgets, animaciones y juegos.',
    '',
    'Devuelve SOLO JSON valido, sin markdown ni explicaciones, con esta forma exacta:',
    '{"translations":{"fieldKey":{"en":"...","fr":"..."}}}',
    '',
    'Reglas:',
    '- Mantén exactamente las mismas claves de campo.',
    '- Usa exactamente estos codigos de idioma: ' + targets.map((l) => l.code).join(', '),
    `- Las claves que acaban en _title son nombres: muy cortos, como mucho ${M.LIMITS.title} caracteres.`,
    `- Las claves que acaban en _text son frases: como mucho ${M.LIMITS.text} caracteres.`,
    '- No traduzcas marcas ni nombres propios (TikControl, TikTok, LIVE, nombres de juegos).',
    '- Tono natural y cercano, como el de una app; nada de traducciones literales.',
    '- Texto plano: sin HTML, sin markdown y sin claves extra.',
    '- No uses comillas dobles rectas (") dentro de los textos: usa comillas tipograficas (« », “ ”) o escapalas como \\".',
    '',
    JSON.stringify({ sourceLanguage: 'es', targetLanguages: targets, fields: Object.fromEntries(fields.map((f) => [f.id, f.es])) }, null, 2)
  ].join('\n');
}

// El JSON pegado → el formulario con las traducciones puestas.
// { form, applied: cuántos textos, done: sus ids }
function applyTranslations(form, fields, parsed) {
  const root = parsed && typeof parsed === 'object' ? parsed : {};
  const t = root.translations && typeof root.translations === 'object' ? root.translations : root;
  const next = { ...form };
  const done = [];
  fields.forEach((f) => {
    const got = t[f.id];
    if (!got || typeof got !== 'object') return;
    const index = next[f.section].findIndex((item) => item.key === f.key);
    if (index === -1) return;
    const value = { ...next[f.section][index][f.field] };
    // El español ha cambiado desde que se preparó el prompt: esa traducción ya no es la suya.
    if (String(value.es || '').trim() !== f.es) return;
    let any = false;
    ALL_LANGS.forEach(({ id }) => {
      if (id === 'es' || typeof got[id] !== 'string' || !got[id].trim()) return;
      if (id === 'en' && String(value.en || '').trim()) return;   // el inglés escrito a mano manda
      value[id] = got[id];
      any = true;
    });
    if (!any) return;
    next[f.section] = next[f.section].map((item, i) => (i === index ? { ...item, [f.field]: value } : item));
    done.push(fieldId(f.section, f.key, f.field));
  });
  return { form: M.sanitize(next), applied: done.length, done };
}

// ── Catálogos (lo que se puede elegir en cada carrusel) ──────────
// `full`: la imagen de la app llena la tarjeta del carrusel (juegos) o va
// sobre un fondo (widgets y animaciones). `cover`: en las miniaturas del
// formulario y del selector, la imagen se recorta (pósteres, carátulas) o se
// ve entera sobre los dos colores del widget.
const WIDGET_OPTIONS = CATALOG.filter((w) => !w.animation && !w.disabled && !w.devOnly).map((w) => {
  const look = M.widgetLook(w.key);
  return { key: w.key, name: w.title, sub: '', thumb: thumbFor(w) || '', colors: look.colors, full: false, cover: false };
});

const ANIMATIONS = CATALOG.filter((w) => w.animation && w.animType && !w.disabled && !w.devOnly && !NO_PACKS.has(w.pack)).map((w) => {
  const [name, ...rest] = String(w.title || w.key).split(' - ');
  return { w, name, sub: rest.join(' - ') };
});
// Los filtros del selector van por el tipo y el pack de verdad, no por el título: «Quiéreme
// Payaso» es una Quiéreme y «Guantes Mano», unos Guantes. Cada filtro se llama como la
// mayoría de los suyos («Quiéreme», «Pack Halloween»).
function commonLabels(field, text) {
  const counts = new Map();
  ANIMATIONS.forEach((a) => {
    const votes = counts.get(a.w[field]) || new Map();
    votes.set(a[text], (votes.get(a[text]) || 0) + 1);
    counts.set(a.w[field], votes);
  });
  return new Map([...counts].map(([id, votes]) => [id, [...votes].sort((x, y) => y[1] - x[1])[0][0]]));
}
const TYPE_LABEL = commonLabels('animType', 'name');
const PACK_LABEL = commonLabels('pack', 'sub');
const ANIMATION_OPTIONS = ANIMATIONS.map(({ w, name, sub }) => ({
  key: w.key, name, sub, thumb: thumbFor(w) || '', full: false, cover: true, type: TYPE_LABEL.get(w.animType), pack: PACK_LABEL.get(w.pack)
}));

function gameOptions(manifest) {
  return (manifest?.games || [])
    .filter((g) => g && g.id && !g.devOnly && !COMING.has(g.status))
    .map((g) => ({
      key: g.id,
      name: g.name || g.id,
      sub: '',
      thumb: g.banner ? `${GAMES_BASE}/${String(g.banner).replace(/\.(jpg|jpeg|png|webp)$/i, '-thumb.webp')}` : '',
      full: true,
      cover: true
    }));
}

const ICONS = {
  widgets: <><rect x="3" y="3" width="7.5" height="7.5" rx="2" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="2" /><rect x="3" y="13.5" width="7.5" height="7.5" rx="2" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" /></>,
  animations: <><rect x="3" y="4" width="18" height="16" rx="2.5" /><path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4" /><path d="m10.5 9.5 4 2.5-4 2.5z" /></>,
  games: <><path d="M6 12h4M8 10v4M15 13h.01M18 11h.01" /><path d="M17.3 6H6.7A4.7 4.7 0 0 0 2 10.7v2.6A4.7 4.7 0 0 0 6.7 18c1.6 0 2.5-1 3.3-2h4c.8 1 1.7 2 3.3 2a4.7 4.7 0 0 0 4.7-4.7v-2.6A4.7 4.7 0 0 0 17.3 6z" /></>
};

function Icon({ children, size = 18 }) {
  return (
    <svg className="hm-ico" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}
const ARROW = <><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></>;
const CHECK = <path d="m5 12.5 4.5 4.5L19 7.5" />;
const CLOSE = <><path d="M6 6l12 12" /><path d="M18 6 6 18" /></>;

// Los vídeos de un juego que valen (enlace de TikTok), como los guardará el modelo.
const validVideos = (item) => M.cleanVideos((item && item.videos) || []);
// 552100 → «552.1K», como en la app.
function compact(n) {
  const v = Number(n) || 0;
  const one = (x) => String(Math.round(x * 10) / 10);
  return v >= 1e6 ? `${one(v / 1e6)}M` : v >= 1e3 ? `${one(v / 1e3)}K` : String(v);
}

// La miniatura de un elemento, en el formulario y en el selector: la imagen
// subida o la de la app (la de un widget, entera sobre sus dos colores).
function Thumb({ sectionId, option, image = '', className = '' }) {
  const [broken, setBroken] = useState('');
  const src = image || option?.thumb || '';
  const cover = !!image || !!option?.cover;
  const colors = !cover && option?.colors;
  return (
    <span
      className={`relative grid shrink-0 place-items-center overflow-hidden rounded-md bg-default-100 text-default-400 ${className}`}
      style={colors ? { background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})` } : undefined}
    >
      {src && broken !== src
        ? <img src={src} alt="" loading="lazy" decoding="async" draggable="false" onError={() => setBroken(src)} className={`h-full w-full ${cover ? 'object-cover' : 'object-contain p-[7%]'}`} />
        : <span className={colors ? 'text-white/80' : ''}><Icon size={20}>{ICONS[sectionId]}</Icon></span>}
    </span>
  );
}

function Chips({ label, value, values, onChange }) {
  const chip = (active) => `rounded-full border px-2.5 py-1 text-xs font-semibold transition ${active ? 'border-primary bg-primary/15 text-primary' : 'border-default-200 text-default-600 hover:border-default-400'}`;
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={label}>
      <span className="mr-1 w-[72px] shrink-0 text-xs font-semibold uppercase tracking-wide text-default-500">{label}</span>
      <button type="button" className={chip(!value)} aria-pressed={!value} onClick={() => onChange('')}>Todos</button>
      {values.map((v) => (
        <button key={v} type="button" className={chip(value === v)} aria-pressed={value === v} onClick={() => onChange(value === v ? '' : v)}>{v}</button>
      ))}
    </div>
  );
}

// ── El selector: los elementos, con su imagen ────────────────────
// Se pulsan los que se quieran (el número es el orden en que entran) y se
// añaden todos a la vez. Los que ya están en el carrusel salen marcados.
function Picker({ section, options, used, room, loading, onAdd, onClose, max = M.MAX_ITEMS, where = 'el carrusel' }) {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [pack, setPack] = useState('');
  const [chosen, setChosen] = useState([]);
  const types = useMemo(() => uniq(options.map((o) => o.type)), [options]);
  const packs = useMemo(() => uniq(options.map((o) => o.pack)), [options]);
  const q = fold(query.trim());
  const visible = options.filter((o) => (!type || o.type === type) && (!pack || o.pack === pack) && (!q || fold(`${o.name} ${o.sub} ${o.key}`).includes(q)));
  const left = room - chosen.length;
  const tall = section.id === 'animations';
  const toggle = (key) => setChosen((list) => (list.includes(key) ? list.filter((k) => k !== key) : list.length < room ? [...list, key] : list));

  return (
    <Modal isOpen onOpenChange={(isOpen) => { if (!isOpen) onClose(); }} size="5xl" scrollBehavior="inside" placement="center">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span>Añadir a «{section.label}»</span>
            <span className="text-xs font-normal text-default-500">Pulsa los que quieras; el número es el orden en que entran.</span>
          </div>
          <Input
            size="sm" type="search" aria-label="Buscar" placeholder="Buscar por nombre…" autoFocus isClearable
            value={query} onValueChange={setQuery} onClear={() => setQuery('')}
          />
          {types.length > 1 ? <Chips label="Animación" value={type} values={types} onChange={setType} /> : null}
          {packs.length > 1 ? <Chips label="Pack" value={pack} values={packs} onChange={setPack} /> : null}
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div className="flex justify-center p-10"><Spinner /></div>
          ) : visible.length ? (
            <div className={`grid gap-3 pb-1 ${tall ? 'grid-cols-[repeat(auto-fill,minmax(118px,1fr))]' : 'grid-cols-[repeat(auto-fill,minmax(176px,1fr))]'}`}>
              {visible.map((o) => {
                const isUsed = used.has(norm(o.key));
                const order = chosen.indexOf(o.key) + 1;
                const blocked = isUsed || (!order && left <= 0);
                return (
                  <button
                    key={o.key} type="button" disabled={blocked} aria-pressed={!!order} title={isUsed ? 'Ya está' : o.key}
                    onClick={() => toggle(o.key)}
                    className={`relative flex flex-col gap-2 rounded-xl border p-2 text-left transition disabled:cursor-not-allowed ${order ? 'border-primary bg-primary/10' : 'border-default-200 enabled:hover:border-default-400 enabled:hover:bg-default-100/60'} ${blocked ? 'opacity-45' : ''}`}
                  >
                    <Thumb sectionId={section.id} option={o} className={`w-full ${tall ? 'aspect-[9/16]' : 'aspect-video'}`} />
                    {order ? <span className="absolute right-3.5 top-3.5 grid h-6 min-w-6 place-items-center rounded-full bg-primary px-1.5 text-xs font-bold text-white shadow">{order}</span> : null}
                    {isUsed ? (
                      <span className="absolute left-3.5 top-3.5 flex items-center gap-1 rounded-md bg-black/75 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                        <Icon size={12}>{CHECK}</Icon>Ya está
                      </span>
                    ) : null}
                    <span className="min-w-0 px-0.5">
                      <span className="line-clamp-2 text-sm font-semibold leading-tight" title={o.name}>{o.name}</span>
                      {o.sub ? <span className="block truncate text-xs text-default-500">{o.sub}</span> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-default-500">
              {options.length ? 'Nada coincide con lo que buscas.' : 'No hay nada que elegir en este catálogo.'}
            </p>
          )}
        </ModalBody>
        <ModalFooter className="items-center">
          <span className="mr-auto text-sm text-default-500" role="status">
            {chosen.length ? `${chosen.length} ${chosen.length === 1 ? 'elegido' : 'elegidos'}` : 'Ninguno elegido'}
            {' · '}{left > 0 ? `${left === 1 ? 'cabe 1' : `caben ${left}`} más` : `${where} se llena (máximo ${max})`}
          </span>
          <Button variant="light" onPress={onClose}>Cancelar</Button>
          <Button color="primary" isDisabled={!chosen.length} onPress={() => onAdd(chosen)}>
            {chosen.length > 1 ? `Añadir ${chosen.length}` : 'Añadir'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Vista previa: el carrusel, como en la app ────────────────────
function Preview({ section, items, options, lang, selected, onSelect }) {
  const byKey = useMemo(() => new Map(options.map((o) => [norm(o.key), o])), [options]);
  const cards = items.map((item, index) => ({ item, index, option: byKey.get(norm(item.key)) })).filter((c) => c.option);
  return (
    <div className="hm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", gap: 0 }}>
      <div className="hm-showcase">
        <section className={`hm-sc hm-sc--${section.id}`}>
          <header className="hm-sc-head">
            <span className="hm-sc-ico" aria-hidden="true"><Icon size={30}>{ICONS[section.id]}</Icon></span>
            <div className="hm-sc-headcopy"><h2>{section.title}</h2><p>{section.sub}</p></div>
            <span className="hm-link hm-sc-all"><span>{section.all}</span><Icon size={15}>{ARROW}</Icon></span>
          </header>
          <div className="hm-sc-row">
            <div className="hm-sc-track" role="list">
              {cards.map(({ item, index, option }) => {
                const image = item.image || option.thumb;
                const full = !!item.image || option.full;
                const title = M.localized(item.title, lang) || option.name;
                const text = M.localized(item.text, lang) || option.sub;
                const look = !full && option.colors ? { '--c1': option.colors[0], '--c2': option.colors[1] } : undefined;
                return (
                  <article
                    key={item.key}
                    role="listitem"
                    className="hm-sc-card"
                    onClick={() => onSelect(index)}
                    style={selected === index ? { outline: '2px dashed rgba(116, 197, 255, 0.7)', outlineOffset: 6, borderRadius: 14 } : undefined}
                  >
                    <span className={`hm-sc-img ${full ? 'is-full' : 'is-thumb'}`} style={look}>
                      {image ? <img src={image} alt="" draggable="false" /> : null}
                      <Icon size={30}>{ICONS[section.id]}</Icon>
                    </span>
                    <div className="hm-sc-cap">
                      <div className="hm-sc-copy">
                        <strong title={title}>{title}</strong>
                        {text ? <span title={text}>{text}</span> : null}
                      </div>
                      <button type="button" className="hm-btn hm-btn--primary hm-btn--sm hm-sc-act" tabIndex={-1}>
                        <span>{section.action}</span><Icon size={14}>{ARROW}</Icon>
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

// Las traducciones de un elemento: cuántos idiomas tiene y, desplegado, cada
// una, para verla y corregirla.
function Translations({ item, stale, canWrite, onChange }) {
  const [show, setShow] = useState(false);
  const fields = TEXT_FIELDS.filter(([field]) => String(item[field].es || '').trim());
  if (!fields.length) {
    return <p className="text-xs text-default-500">Sin texto propio en español: en todos los idiomas sale el que ya tiene en la app.</p>;
  }
  const done = OTHER_LANGS.filter((l) => fields.every(([field]) => String(item[field][l.id] || '').trim()));
  const whole = done.length === OTHER_LANGS.length;
  return (
    <div className="rounded-lg border border-default-200 p-2.5" data-translations>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-default-500">Otros idiomas</span>
        <span className={`text-xs ${stale ? 'text-warning' : whole ? 'text-success' : 'text-warning'}`}>
          {stale
            ? 'Has cambiado el español: vuelve a traducir'
            : whole ? `Traducido a los ${OTHER_LANGS.length}` : done.length ? `${done.length} de ${OTHER_LANGS.length} traducidos` : 'Sin traducir: se ven en inglés'}
        </span>
        <button type="button" className="ml-auto text-xs font-semibold text-primary" aria-expanded={show} onClick={() => setShow(!show)}>
          {show ? 'Ocultar' : 'Ver y corregir'}
        </button>
      </div>
      {show ? (
        <div className="mt-2 space-y-1.5">
          {OTHER_LANGS.map((l) => (
            <div key={l.id} className="grid grid-cols-[70px_minmax(0,1fr)_minmax(0,1.6fr)] items-center gap-2">
              <span className="truncate text-xs text-default-500" title={l.name}>{l.label}</span>
              {TEXT_FIELDS.map(([field, label]) => {
                const has = !!String(item[field].es || '').trim();
                return (
                  <input
                    key={field} dir="auto" className={MINI_FIELD} aria-label={`${label} · ${l.name}`} disabled={!has} readOnly={!canWrite}
                    maxLength={Math.round(M.LIMITS[field] * M.TRANSLATION_SLACK)} placeholder={has ? 'Se ve en inglés' : '—'}
                    value={item[field][l.id] || ''} onChange={(e) => onChange({ ...item, [field]: { ...item[field], [l.id]: e.target.value } })}
                  />
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── Vista previa: el modal de un juego con vídeos, como en la app ─
function GameModalPreview({ item, option, lang }) {
  const videos = validVideos(item);
  const title = M.localized(item.title, lang) || option?.name || item.key;
  const text = M.localized(item.text, lang);
  const art = item.image || option?.thumb || '';
  return (
    <div className="hm-game" role="img" aria-label="Vista previa del modal del juego" style={{ animation: 'none', margin: '0 auto' }}>
      <span className="hm-game-x" aria-hidden="true"><Icon>{CLOSE}</Icon></span>
      <div className="hm-game-head">
        {art ? <span className="hm-game-art"><img src={art} alt="" /></span> : null}
        <div className="hm-game-id">
          <span className="hm-game-kicker"><Icon size={14}>{ICONS.games}</Icon>Gaming</span>
          <h3 className="hm-game-title">{title}</h3>
          {text ? <p className="hm-game-text">{text}</p> : null}
        </div>
      </div>
      <div className="hm-game-sec">
        <h4>{lang === 'es' ? 'Mira cómo se juega' : 'See how it plays'}</h4>
        <p>{lang === 'es' ? 'Vídeos del juego en TikTok. Se abren en tu navegador.' : 'Videos of the game on TikTok. They open in your browser.'}</p>
      </div>
      <div className="hm-game-videos" data-n={videos.length}>
        {videos.map((video) => (
          <span key={video.id} className="hm-game-video" title={video.title || video.url}>
            {video.cover ? <img src={video.cover} alt="" /> : null}
            <span className="hm-game-video-play" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
            </span>
            {video.views > 0 ? <span className="hm-game-video-views">▶ {compact(video.views)}</span> : null}
          </span>
        ))}
      </div>
      <div className="hm-game-actions">
        <button type="button" className="hm-btn hm-btn--primary hm-game-go" tabIndex={-1}><span>{lang === 'es' ? 'Ir al juego' : 'Go to the game'}</span><Icon size={16}>{ARROW}</Icon></button>
      </div>
    </div>
  );
}

// Los vídeos de TikTok de un juego (hasta tres): enlace y visualizaciones.
function GameVideos({ item, canWrite, onChange }) {
  const videos = Array.isArray(item.videos) ? item.videos : [];
  const set = (next) => onChange({ ...item, videos: next });
  const edit = (index, patch) => set(videos.map((video, i) => (i === index ? { ...video, ...patch } : video)));
  return (
    <div className="space-y-2 rounded-lg border border-default-200 p-2.5" data-game-videos>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-default-500">Vídeos de TikTok</span>
        <span className="text-xs text-default-500">
          {validVideos(item).length ? 'Al pulsar su tarjeta se abre un modal con ellos y «Ir al juego».' : 'Sin vídeos, su tarjeta va directa a la ficha del juego.'}
        </span>
      </div>
      {videos.map((video, index) => {
        const url = String(video.url || '');
        const valid = !url.trim() || !!M.parseVideoUrl(url);
        return (
          <div key={index} className="flex items-start gap-2">
            <span className="grid h-[52px] w-[30px] shrink-0 place-items-center overflow-hidden rounded bg-default-100 text-[10px] text-default-400" title={video.cover ? 'Portada' : 'La portada se pone al guardar'}>
              {video.cover ? <img src={video.cover} alt="" className="h-full w-full object-cover" /> : '▶'}
            </span>
            <Input
              size="sm" className="min-w-0 flex-1" aria-label={`Enlace del vídeo ${index + 1}`} placeholder="https://www.tiktok.com/@tikcontrol.app/video/…" isReadOnly={!canWrite}
              isInvalid={!valid} errorMessage={valid ? undefined : 'No es el enlace de un vídeo de TikTok (…/@usuario/video/número).'}
              // Otro enlace es otro vídeo: su portada y su título se piden de nuevo al guardar.
              value={url} onValueChange={(next) => edit(index, { url: next, ...(next.trim() !== url.trim() ? { id: '', cover: '', title: '' } : {}) })}
            />
            <Input
              size="sm" className="w-28 shrink-0" aria-label={`Visualizaciones del vídeo ${index + 1}`} placeholder="552.1K" isReadOnly={!canWrite}
              value={video.views ? String(video.views) : ''} onValueChange={(views) => edit(index, { views })}
            />
            {canWrite ? <Button size="sm" variant="light" color="danger" isIconOnly aria-label="Quitar vídeo" onPress={() => set(videos.filter((_, i) => i !== index))}>✕</Button> : null}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-3">
        {canWrite && videos.length < M.MAX_VIDEOS ? (
          <Button size="sm" variant="flat" onPress={() => set([...videos, { url: '', views: '' }])} data-add-video>Añadir vídeo</Button>
        ) : null}
        <span className="text-xs text-default-500">Hasta {M.MAX_VIDEOS}. Pega el enlace y sus visualizaciones (552.1K, 1,2M…); la portada se pone sola al guardar.</span>
      </div>
    </div>
  );
}

// ── Un elemento del formulario ───────────────────────────────────
function ItemRow({ section, item, option, index, count, open, canWrite, uploading, stale, onToggle, onChange, onMove, onRemove, onUpload }) {
  const fileRef = useRef(null);
  const tall = section.id === 'animations';
  const setLang = (field, lang, value) => onChange({ ...item, [field]: { ...item[field], [lang]: value } });
  return (
    <li className={`rounded-lg border ${open ? 'border-primary-400' : 'border-default-200'} bg-content1`}>
      <div className="flex items-center gap-3 p-2">
        <span className="w-5 shrink-0 text-center text-xs font-semibold text-default-400">{index + 1}</span>
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <Thumb sectionId={section.id} option={option} image={item.image} className={tall ? 'h-16 w-9' : 'h-10 w-[72px]'} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">{item.title.es || option?.name || item.key}</span>
            <span className="block truncate text-xs text-default-500">
              {option ? <code>{item.key}</code> : <span className="text-warning">No está en el catálogo: la app se lo salta</span>}
              {item.image ? ' · imagen propia' : option ? ' · imagen de la app' : ''}
            </span>
          </span>
        </button>
        {canWrite ? (
          <div className="flex shrink-0 gap-1">
            <Button size="sm" variant="light" isIconOnly aria-label="Subir" isDisabled={index === 0} onPress={() => onMove(-1)}>↑</Button>
            <Button size="sm" variant="light" isIconOnly aria-label="Bajar" isDisabled={index === count - 1} onPress={() => onMove(1)}>↓</Button>
            <Button size="sm" variant="light" color="danger" isIconOnly aria-label="Quitar" onPress={onRemove}>✕</Button>
          </div>
        ) : null}
      </div>

      {open ? (
        <div className="space-y-3 border-t border-default-200 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-default-500">Imagen ({section.shape})</span>
            {canWrite ? (
              <>
                <Button size="sm" variant="flat" color="primary" isLoading={uploading} onPress={() => fileRef.current?.click()}>
                  {item.image ? 'Cambiar imagen' : 'Subir imagen'}
                </Button>
                {item.image ? <Button size="sm" variant="light" onPress={() => onChange({ ...item, image: '' })}>Usar la de la app</Button> : null}
                <input
                  ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                  onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) onUpload(file); }}
                />
              </>
            ) : null}
            <span className="text-xs text-default-500">
              Se recorta al centro y se guarda a {M.IMAGE[section.id].width} × {M.IMAGE[section.id].height} px.
            </span>
          </div>
          {LANGS.map((lang) => (
            <div key={lang.id} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
              <Input
                size="sm" label={`Nombre · ${lang.label}`} labelPlacement="outside" maxLength={M.LIMITS.title} isReadOnly={!canWrite}
                placeholder={option?.name || 'El nombre de la app'} value={item.title[lang.id]} onValueChange={(v) => setLang('title', lang.id, v)}
              />
              <Input
                size="sm" label={`Frase · ${lang.label}`} labelPlacement="outside" maxLength={M.LIMITS.text} isReadOnly={!canWrite}
                placeholder={option?.sub || 'Sale al pasar el ratón (opcional)'} value={item.text[lang.id]} onValueChange={(v) => setLang('text', lang.id, v)}
                description={`${item.text[lang.id].length} / ${M.LIMITS.text}`}
              />
            </div>
          ))}
          <Translations item={item} stale={stale} canWrite={canWrite} onChange={onChange} />
          {section.id === 'games' ? <GameVideos item={item} canWrite={canWrite} onChange={onChange} /> : null}
        </div>
      ) : null}
    </li>
  );
}

// ── Destacados de la Galería de overlays ─────────────────────────
// La tarjeta, como la pinta la app: su imagen (o el icono de su familia) y, debajo, el tipo y el nombre.
function FeaturedCard({ entry, look, option, lang, selected, onSelect }) {
  const [broken, setBroken] = useState('');
  const image = look.image || option?.thumb || '';
  const title = M.localized(look.title, lang) || option?.name || entry.key;
  const tint = { widgets: 'linear-gradient(135deg, #0a2140, #0b1630)', animations: 'linear-gradient(135deg, #1d1140, #33102f)', goals: 'linear-gradient(135deg, #06302c, #0b2140)' }[entry.kind];
  return (
    <button
      type="button" onClick={onSelect}
      className="flex min-w-0 flex-col overflow-hidden rounded-[14px] border text-left"
      style={{ borderColor: selected ? '#38bde8' : '#1c2940', background: '#0b1322', color: '#fff', fontFamily: "Inter, system-ui, sans-serif" }}
    >
      <span className="relative grid aspect-video place-items-center overflow-hidden" style={{ background: tint, color: 'rgba(159, 177, 205, 0.4)' }}>
        <Icon size={44}>{ICONS[entry.kind === 'goals' ? 'widgets' : entry.kind]}</Icon>
        {image && broken !== image ? <img src={image} alt="" draggable="false" onError={() => setBroken(image)} className={`absolute inset-0 h-full w-full ${look.image ? 'object-cover' : 'object-contain'}`} /> : null}
      </span>
      <span className="flex min-w-0 items-center gap-2 px-3.5 py-3">
        <span className="shrink-0 rounded-md border px-1.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wide" style={{ borderColor: '#2a3852', color: '#9fb1cd' }}>{KIND_LABEL[entry.kind]}</span>
        <span className="min-w-0 flex-1 truncate text-[15px] font-bold" title={title}>{title}</span>
      </span>
    </button>
  );
}

function FeaturedRow({ entry, option, twin, twinName, index, count, open, canWrite, uploading, stale, onToggle, onChange, onMove, onRemove, onUpload, onGoHome }) {
  const fileRef = useRef(null);
  const size = M.IMAGE[entry.kind];
  const setLang = (lang, value) => onChange({ ...entry, title: { ...entry.title, [lang]: value } });
  const shown = twin ? twin.image : entry.image;
  const name = (twin ? twin.title.es : entry.title.es) || option?.name || entry.key;
  return (
    <li className={`rounded-lg border ${open ? 'border-primary-400' : 'border-default-200'} bg-content1`} data-featured-row>
      <div className="flex items-center gap-3 p-2">
        <span className="w-5 shrink-0 text-center text-xs font-semibold text-default-400">{index + 1}</span>
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <Thumb sectionId={entry.kind === 'goals' ? 'widgets' : entry.kind} option={option} image={shown} className={entry.kind === 'animations' && !shown ? 'h-16 w-9' : 'h-10 w-[72px]'} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">{name}</span>
            <span className="block truncate text-xs text-default-500">
              {KIND_LABEL[entry.kind]} · {option ? <code>{entry.key}</code> : <span className="text-warning">No está en el catálogo: la app se lo salta</span>}
              {twin ? ` · como en «${twinName}»` : shown ? ' · imagen propia' : entry.kind === 'goals' ? ' · sin imagen (su icono)' : option ? ' · imagen de la app' : ''}
            </span>
          </span>
        </button>
        {canWrite ? (
          <div className="flex shrink-0 gap-1">
            <Button size="sm" variant="light" isIconOnly aria-label="Subir" isDisabled={index === 0} onPress={() => onMove(-1)}>↑</Button>
            <Button size="sm" variant="light" isIconOnly aria-label="Bajar" isDisabled={index === count - 1} onPress={() => onMove(1)}>↓</Button>
            <Button size="sm" variant="light" color="danger" isIconOnly aria-label="Quitar" onPress={onRemove}>✕</Button>
          </div>
        ) : null}
      </div>
      {open ? (
        <div className="space-y-3 border-t border-default-200 p-3">
          {twin ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="min-w-0 flex-1 text-sm text-default-600">
                También está en el carrusel «{twinName}» del Inicio: aquí se ve con <strong>la misma imagen y el mismo nombre</strong>. Cámbialos allí y cambian en los dos sitios.
              </p>
              <Button size="sm" variant="flat" onPress={onGoHome}>Ir a su tarjeta del Inicio</Button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-default-500">Imagen</span>
                {canWrite ? (
                  <>
                    <Button size="sm" variant="flat" color="primary" isLoading={uploading} onPress={() => fileRef.current?.click()}>
                      {entry.image ? 'Cambiar imagen' : 'Subir imagen'}
                    </Button>
                    {entry.image ? <Button size="sm" variant="light" onPress={() => onChange({ ...entry, image: '' })}>{entry.kind === 'goals' ? 'Quitar imagen' : 'Usar la de la app'}</Button> : null}
                    <input
                      ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                      onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) onUpload(file); }}
                    />
                  </>
                ) : null}
                <span className="text-xs text-default-500">Se recorta al centro y se guarda a {size.width} × {size.height} px. Si este elemento entra después en su carrusel del Inicio, se usa también allí.</span>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {LANGS.map((lang) => (
                  <Input
                    key={lang.id} size="sm" label={`Nombre · ${lang.label}`} labelPlacement="outside" maxLength={M.LIMITS.title} isReadOnly={!canWrite}
                    placeholder={option?.name || 'El nombre de la app'} value={entry.title[lang.id] || ''} onValueChange={(v) => setLang(lang.id, v)}
                  />
                ))}
              </div>
              <p className={`text-xs ${stale ? 'text-warning' : 'text-default-500'}`}>
                {!String(entry.title.es || '').trim()
                  ? 'Sin nombre propio: sale el que ya tiene en la app, en el idioma de cada usuario.'
                  : stale ? 'Has cambiado el español: vuelve a traducir (botón «Traducir con ChatGPT» de arriba).'
                    : M.missingLangs(entry.title).length ? 'Sin traducir a los demás idiomas: se ve en inglés. Usa «Traducir con ChatGPT», arriba.' : `Traducido a los ${OTHER_LANGS.length} idiomas restantes.`}
              </p>
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}

function TabMark({ dirty, conflict }) {
  if (!dirty && !conflict) return null;
  return (
    <span
      className={`ml-2 inline-block h-2 w-2 rounded-full align-middle ${conflict ? 'bg-danger' : 'bg-warning'}`}
      title={conflict ? 'Otro admin la ha guardado mientras la editabas' : 'Cambios sin guardar'}
      aria-label={conflict ? 'Otro admin la ha guardado mientras la editabas' : 'Cambios sin guardar'}
      data-tab-dirty={conflict ? 'conflict' : 'dirty'}
    />
  );
}

export default function HomeShowcaseEditor({ canWrite }) {
  // { form: lo que hay en pantalla, bases: de qué versión del servidor parte cada pestaña,
  //   conflicts: pestañas que otro ha guardado mientras se editaban, server: lo último leído }
  const [doc, setDoc] = useState(null);
  const form = doc ? doc.form : null;
  const setForm = useCallback((next) => setDoc((d) => (d ? { ...d, form: typeof next === 'function' ? next(d.form) : next } : d)), []);
  const docRef = useRef(null);
  docRef.current = doc;
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');       // «Gaming se ha actualizado con lo que guardó …»
  const [manifest, setManifest] = useState(null);
  const [tab, setTab] = useState(SECTIONS[0].id);
  const [open, setOpen] = useState(0);             // elemento desplegado del carrusel a la vista
  const [lang, setLang] = useState('es');
  const [picking, setPicking] = useState(false);   // el selector de elementos, abierto
  const [pickingKind, setPickingKind] = useState('');   // destacados: de qué tipo se está eligiendo ('' = cerrado)
  const [translating, setTranslating] = useState(false);   // el asistente de traducción, abierto
  const [stale, setStale] = useState(() => new Set());     // textos cambiados en español después de traducirlos
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(-1);
  const [message, setMessage] = useState(null);    // { type: 'ok' | 'warn' | 'error', text }

  useEffect(() => {
    // La fuente de la app, para que la vista previa mida lo mismo.
    if (!document.querySelector(`link[href="${APP_FONT_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = APP_FONT_URL;
      document.head.appendChild(link);
    }
  }, []);

  // Quién guardó (con el rol de solo lectura, el correo enmascarado).
  const who = useCallback((meta) => {
    if (!meta || (!meta.by && !meta.email)) return '';
    if (meta.by && auth.currentUser && meta.by === auth.currentUser.uid) return 'ti';
    if (meta.email) return canWrite ? meta.email : maskEmail(meta.email);
    return 'otro admin';
  }, [canWrite]);

  // Lee lo guardado y lo junta con lo que hay en pantalla (ver mergeFresh).
  const refresh = useCallback(async () => {
    let data;
    try {
      data = await apiFetch(ENDPOINT, { cache: 'no-store' });
    } catch (e) {
      // Sin la primera lectura no se puede guardar nada (no se sabe de qué se parte).
      if (!docRef.current) setLoadError(e.message || 'No se pudo leer');
      return;
    }
    setLoadError('');
    const before = docRef.current;
    // Una lectura que salió antes de un guardado y llega después: no dice nada nuevo.
    if (before && isStale(before, data)) return;
    if (before) {
      // Qué pestañas sin cambios han cambiado en el servidor: lo guardó otro.
      const changed = changedByOthers(before, data);
      if (changed.length) {
        const by = who(data.sections && data.sections[changed[0]]);
        const at = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        setNotice(`${at}: ${changed.map((s) => `«${SECTION_LABEL[s]}»`).join(', ')} ${changed.length === 1 ? 'se ha actualizado' : 'se han actualizado'} con lo que ${by && by !== 'ti' ? `guardó ${by}` : 'se guardó en otra página'}.`);
      }
    }
    setDoc((d) => (d ? mergeFresh(d, data) : freshDoc(data)));
  }, [who]);

  useEffect(() => {
    let alive = true;
    refresh();
    // El catálogo de juegos, el mismo que descarga la app.
    apiFetch(`/api/games/manifest?backoffice=${Date.now()}`, { cache: 'no-store' })
      .then((data) => { if (alive) setManifest(data); })
      .catch(() => { if (alive) setManifest({ games: [] }); });
    return () => { alive = false; };
  }, [refresh]);

  // Lo que guardan los demás: cada 30 s con la página a la vista y al volver a ella.
  useEffect(() => {
    let last = Date.now();
    const tick = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 5000) return;
      last = Date.now();
      refresh();
    };
    const timer = setInterval(tick, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  // Cambios sin guardar: el navegador avisa antes de cerrar o recargar la página.
  const anyDirty = !!doc && SAVE_SECTIONS.some((s) => isDirty(doc, s));
  useEffect(() => {
    if (!anyDirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyDirty]);

  const options = useMemo(() => ({ widgets: WIDGET_OPTIONS, animations: ANIMATION_OPTIONS, games: gameOptions(manifest) }), [manifest]);

  if (!form) {
    return loadError ? (
      <section className="space-y-3 rounded-xl border border-default-200 bg-content1 p-5">
        <h2 className="text-lg font-semibold">Carruseles del Inicio</h2>
        <p className="text-sm text-danger">No se pudo leer lo guardado: {loadError}</p>
        <Button size="sm" variant="flat" onPress={refresh}>Reintentar</Button>
      </section>
    ) : <div className="flex justify-center p-8"><Spinner /></div>;
  }

  const isGallery = tab === GALLERY_TAB;
  const section = SECTIONS.find((s) => s.id === tab) || SECTIONS[0];
  const items = isGallery ? [] : form[tab];
  const sectionOptions = isGallery ? [] : options[tab];
  // Destacados de la galería.
  const featured = form.gallery || [];
  const featuredOptions = { widgets: options.widgets, animations: options.animations, goals: GOAL_OPTIONS };
  const featuredOption = (entry) => featuredOptions[entry.kind].find((o) => norm(o.key) === norm(entry.key));
  const featuredFull = featured.length >= M.MAX_GALLERY;
  const setFeatured = (next) => setForm((f) => ({ ...f, gallery: typeof next === 'function' ? next(f.gallery || []) : next }));
  const changeFeatured = (index, entry) => {
    const before = featured[index];
    if (before && before.title.es !== entry.title.es && OTHER_LANGS.some((l) => String(entry.title[l.id] || '').trim())) {
      setStale((set) => new Set(set).add(fieldId(GALLERY_TAB, entry.key, 'title')));
    }
    setFeatured((list) => list.map((it, i) => (i === index ? entry : it)));
  };
  const moveFeatured = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= featured.length) return;
    setFeatured((list) => { const next = list.slice(); [next[index], next[j]] = [next[j], next[index]]; return next; });
    setOpen(j);
  };
  const addFeatured = (kind, keys) => {
    setPickingKind('');
    const fresh = keys.filter((key) => !featured.some((it) => it.kind === kind && norm(it.key) === norm(key))).slice(0, M.MAX_GALLERY - featured.length);
    if (!fresh.length) return;
    setFeatured((list) => [...list, ...fresh.map((key) => blankFeatured(kind, key))]);
    setOpen(featured.length);
  };
  const byKey = new Map(sectionOptions.map((o) => [norm(o.key), o]));
  const used = new Set(items.map((item) => norm(item.key)));
  const free = sectionOptions.filter((o) => !used.has(norm(o.key)));
  // Cada pestaña, con o sin cambios sin guardar (respecto a la versión de la que parte).
  const dirtyOf = (s) => isDirty(doc, s);
  const dirty = dirtyOf(tab);
  const otherDirty = SAVE_SECTIONS.filter((s) => s !== tab && dirtyOf(s));
  const conflict = doc.conflicts[tab] || null;
  const label = SECTION_LABEL[tab];
  // Quién y cuándo cambió por última vez la pestaña abierta.
  const meta = doc.server && doc.server.sections ? doc.server.sections[tab] : null;
  const stamp = (at) => new Date(at).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const lastChange = meta && meta.at ? `Último cambio en «${label}»: ${stamp(meta.at)}${who(meta) ? ` · ${who(meta)}` : ''}.` : `«${label}» aún no se ha guardado nunca.`;
  const full = items.length >= M.MAX_ITEMS;

  const setItems = (next) => setForm((f) => ({ ...f, [tab]: typeof next === 'function' ? next(f[tab]) : next }));
  const changeItem = (index, item) => {
    // Cambiar el español de un texto ya traducido deja sus traducciones atrás: se apunta para volver a traducirlo.
    const before = items[index];
    const touched = before ? TEXT_FIELDS.map(([field]) => field).filter((field) => before[field].es !== item[field].es && OTHER_LANGS.some((l) => String(item[field][l.id] || '').trim())) : [];
    if (touched.length) setStale((set) => { const next = new Set(set); touched.forEach((field) => next.add(fieldId(tab, item.key, field))); return next; });
    setItems((list) => list.map((it, i) => (i === index ? item : it)));
  };
  const itemStale = (item) => TEXT_FIELDS.some(([field]) => stale.has(fieldId(tab, item.key, field)));

  // Traducciones: qué falta y aplicar lo que devuelve ChatGPT.
  const toTranslate = translationFields(form, { all: false, stale, only: tab });
  const ownTexts = translationFields(form, { all: true, stale, only: tab });
  const applyTranslated = (fields, parsed) => {
    const result = applyTranslations(form, fields, parsed);
    if (!result.applied) return result;
    setForm(result.form);
    setStale((set) => { const next = new Set(set); result.done.forEach((id) => next.delete(id)); return next; });
    setTranslating(false);
    const missing = fields.length - result.applied;
    setMessage({
      type: missing ? 'warn' : 'ok',
      text: `Traducciones puestas en ${result.applied} ${result.applied === 1 ? 'texto' : 'textos'}${missing ? ` (${missing} sin respuesta)` : ''}. Compruébalas con el idioma de la vista previa y guarda «${label}».`
    });
    return result;
  };
  const move = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    setItems((list) => { const next = list.slice(); [next[index], next[j]] = [next[j], next[index]]; return next; });
    setOpen(j);
  };
  const remove = (index) => { setItems((list) => list.filter((_, i) => i !== index)); setOpen(-1); };
  // Lo elegido en el selector entra al final, en el orden en que se pulsó.
  const add = (keys) => {
    setPicking(false);
    const fresh = keys.filter((key) => !used.has(norm(key))).slice(0, M.MAX_ITEMS - items.length);
    if (!fresh.length) return;
    setItems((list) => [...list, ...fresh.map(blankItem)]);
    setOpen(items.length);
  };

  async function upload(index, file) {
    setUploading(index);
    setMessage(null);
    try {
      const blob = await fitImage(file, M.IMAGE[tab]);
      const body = new FormData();
      body.append('file', new File([blob], 'card.webp', { type: 'image/webp' }));
      body.append('section', tab);
      const res = await apiUpload(`${ENDPOINT}/image`, body);
      setItems((list) => list.map((it, i) => (i === index ? { ...it, image: res.url } : it)));
    } catch (e) {
      setMessage({ type: 'error', text: `No se pudo subir la imagen: ${e.message || e}` });
    } finally {
      setUploading(-1);
    }
  }

  async function uploadFeatured(index, file) {
    const entry = featured[index];
    if (!entry) return;
    setUploading(index);
    setMessage(null);
    try {
      const blob = await fitImage(file, M.IMAGE[entry.kind]);
      const body = new FormData();
      body.append('file', new File([blob], 'card.webp', { type: 'image/webp' }));
      body.append('section', entry.kind);
      const res = await apiUpload(`${ENDPOINT}/image`, body);
      setFeatured((list) => list.map((it, i) => (i === index ? { ...it, image: res.url } : it)));
    } catch (e) {
      setMessage({ type: 'error', text: `No se pudo subir la imagen: ${e.message || e}` });
    } finally {
      setUploading(-1);
    }
  }

  // Guarda SOLO la pestaña abierta (las demás no viajan). `force`: encima de lo que otro guardó.
  async function save(force = false) {
    const section = tab;
    const items = M.sanitize(form)[section] || [];
    const sent = { section, json: JSON.stringify(items) };
    setBusy(true);
    setMessage(null);
    try {
      const res = await apiTry(ENDPOINT, { method: 'PUT', body: { section, items, base: doc.bases[section].stamp, force } });
      const data = res.data && typeof res.data === 'object' ? res.data : {};
      if (res.status === 409 && data.code === 'conflict') {
        // Otro la guardó mientras tanto: no se ha pisado. Lo tuyo sigue en pantalla.
        setDoc((d) => mergeFresh(d, data));
        return;
      }
      if (res.status === 409 && data.code === 'reload') {
        setMessage({ type: 'error', text: 'El backoffice se ha actualizado: recarga la página (F5) para guardar.' });
        return;
      }
      if (!res.ok) {
        const busyNow = res.status === 503 || data.code === 'busy';
        throw new Error(busyNow ? 'El servidor está saturado ahora mismo (pasa un rato después de cada actualización). Vuelve a intentarlo en un minuto.' : (data.error || `Error ${res.status}`));
      }
      setDoc((d) => mergeFresh(d, data, sent));
      const noCover = Array.isArray(data.noCover) ? data.noCover.length : 0;
      setMessage(data.unchanged
        ? { type: 'ok', text: `«${SECTION_LABEL[section]}» ya estaba así: no había nada que guardar.` }
        : noCover
          ? { type: 'warn', text: `«${SECTION_LABEL[section]}» guardado, pero TikTok no ha dado la portada de ${noCover === 1 ? '1 vídeo' : `${noCover} vídeos`}: ${noCover === 1 ? 'sale' : 'salen'} sin ella. Comprueba el enlace y vuelve a guardar.` }
          : { type: 'ok', text: `«${SECTION_LABEL[section]}» guardado (las demás pestañas no se han tocado). Las apps abiertas lo enseñan en un minuto, más o menos.` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo guardar' });
    } finally {
      setBusy(false);
    }
  }

  // «Ver lo suyo»: la pestaña vuelve a lo guardado (se descartan los cambios de aquí).
  const discard = () => setDoc((d) => discardSection(d, tab));

  const tone = { ok: 'text-success', warn: 'text-warning', error: 'text-danger' };

  // La fila de «Guardar» de cada pestaña (con el aviso si otro la ha guardado mientras tanto).
  const saveRow = (extra = null) => (
    <div className="space-y-2">
      {conflict ? (
        <div className="space-y-2 rounded-lg border border-warning/60 bg-warning/10 p-3" role="alert" data-showcase-conflict>
          <p className="text-sm text-warning-600 dark:text-warning">
            Mientras editabas, {who(conflict) || 'otro admin'} guardó «{label}»{conflict.at ? ` (${stamp(conflict.at)})` : ''}. Lo tuyo sigue aquí, sin guardar:
            elige qué se queda.
          </p>
          {canWrite ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="flat" onPress={discard} data-conflict-theirs>Ver lo suyo (descartar lo mío)</Button>
              <Button size="sm" color="warning" variant="flat" isLoading={busy} onPress={() => save(true)} data-conflict-mine>Guardar lo mío encima</Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {canWrite ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" color="primary" isLoading={busy} isDisabled={!dirty || !!conflict} onPress={() => save(false)} data-save-section={tab}>
            {tab === GALLERY_TAB ? 'Guardar destacados' : `Guardar «${label}»`}
          </Button>
          {extra}
          {dirty && !message && !conflict ? <span className="text-sm text-warning">Cambios sin guardar en esta pestaña.</span> : null}
          {message ? <p role="status" className={`text-sm ${tone[message.type]}`}>{message.text}</p> : null}
        </div>
      ) : (message ? <p role="status" className={`text-sm ${tone[message.type]}`}>{message.text}</p> : null)}
      {canWrite && otherDirty.length ? (
        <p className="text-xs text-default-500">
          También hay cambios sin guardar en {otherDirty.map((s) => `«${SECTION_LABEL[s]}»`).join(', ')}: cada pestaña se guarda con su botón.
        </p>
      ) : null}
    </div>
  );

  return (
    <section className="space-y-4 rounded-xl border border-default-200 bg-content1 p-5">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold">Carruseles del Inicio</h2>
          <p className="text-sm text-default-500">
            Qué sale en «Widgets», «Animaciones LIVE» y «Gaming», en qué orden, con qué imagen y con qué texto debajo. Un carrusel
            sin elementos no sale vacío: la app lo rellena sola con los más usados. <strong>Cada pestaña se guarda por separado</strong>: guardar
            una no toca las demás, y lo que guarden otros admins aparece solo.
          </p>
        </div>
        <label className="flex shrink-0 items-center gap-2 text-xs font-semibold text-default-500">
          Vista previa en
          <select
            aria-label="Idioma de la vista previa" value={lang} onChange={(e) => setLang(e.target.value)}
            className="h-9 rounded-md border border-default-200 bg-default-100 px-2 text-sm font-normal text-foreground outline-none focus:border-primary"
          >
            {ALL_LANGS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist">
        {SECTIONS.map((s) => (
          <button
            key={s.id} type="button" role="tab" aria-selected={tab === s.id}
            onClick={() => { setTab(s.id); setOpen(0); setPicking(false); setMessage(null); }}
            className={`rounded-lg border px-4 py-2 text-sm font-semibold ${tab === s.id ? 'border-primary bg-primary/15 text-primary' : 'border-default-200 text-default-600'}`}
          >
            {s.label} <span className="ml-1 text-xs opacity-70">{form[s.id].length || 'auto'}</span>
            <TabMark dirty={dirtyOf(s.id)} conflict={!!doc.conflicts[s.id]} />
          </button>
        ))}
        <button
          type="button" role="tab" aria-selected={isGallery} data-gallery-tab
          onClick={() => { setTab(GALLERY_TAB); setOpen(0); setPicking(false); setMessage(null); }}
          className={`rounded-lg border px-4 py-2 text-sm font-semibold ${isGallery ? 'border-primary bg-primary/15 text-primary' : 'border-default-200 text-default-600'}`}
        >
          Galería · Destacados <span className="ml-1 text-xs opacity-70">{featured.length || '—'}</span>
          <TabMark dirty={dirtyOf(GALLERY_TAB)} conflict={!!doc.conflicts[GALLERY_TAB]} />
        </button>
      </div>

      <div className="space-y-1 text-xs" data-section-info>
        <p className="text-default-500">{lastChange}</p>
        {notice ? <p className="text-primary" role="status" data-showcase-notice>{notice}</p> : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-default-200 p-3" data-translate-strip>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">Idiomas de «{label}»</h3>
          <p className={`text-xs ${toTranslate.length ? 'text-warning' : 'text-default-500'}`}>
            {!ownTexts.length
              ? `Sin textos propios: la app usa los nombres que ya tiene, en sus ${ALL_LANGS.length} idiomas.`
              : toTranslate.length
                ? `${toTranslate.length} ${toTranslate.length === 1 ? 'texto sin traducir' : 'textos sin traducir'}: en los demás idiomas se ${toTranslate.length === 1 ? 've' : 'ven'} en inglés.`
                : `Los ${ownTexts.length} textos propios están en los ${ALL_LANGS.length} idiomas de la app.`}
          </p>
        </div>
        {canWrite && ownTexts.length ? (
          <Button size="sm" variant={toTranslate.length ? 'flat' : 'light'} color="primary" onPress={() => setTranslating(true)} data-translate-open>
            {toTranslate.length ? 'Traducir con ChatGPT' : 'Volver a traducir'}
          </Button>
        ) : null}
      </div>

      {isGallery ? (
      <div className="grid gap-5 xl:grid-cols-[minmax(0,460px)_minmax(0,1fr)]" data-gallery-featured>
        <div className="min-w-0 space-y-3">
          <p className="text-sm text-default-500">
            Hasta {M.MAX_GALLERY}. Salen arriba de la <strong>Galería de overlays</strong> de la app, en «Todo»: solo la imagen y el nombre, y un clic lleva a su tarjeta.
            Sin elegir ninguno, esa fila no sale.
          </p>
          {canWrite ? (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-primary/60 bg-primary/5 px-3 py-2.5">
              <span className={`text-sm font-semibold ${featuredFull ? 'text-default-500' : 'text-primary'}`}>{featuredFull ? `Máximo ${M.MAX_GALLERY} destacados` : 'Añadir un destacado:'}</span>
              {M.GALLERY_KINDS.map((kind) => (
                <Button key={kind} size="sm" variant="flat" color="primary" isDisabled={featuredFull} onPress={() => setPickingKind(kind)} data-add-featured={kind}>{KIND_LABEL[kind]}</Button>
              ))}
            </div>
          ) : null}
          {featured.length ? (
            <ol className="space-y-2">
              {featured.map((entry, index) => {
                const at = homeTwinIndex(form, entry);
                return (
                  <FeaturedRow
                    key={`${entry.kind}|${entry.key}`} entry={entry} option={featuredOption(entry)} twin={at === -1 ? null : form[entry.kind][at]} twinName={KIND_HOME[entry.kind]}
                    index={index} count={featured.length} open={open === index} canWrite={canWrite} uploading={uploading === index}
                    stale={stale.has(fieldId(GALLERY_TAB, entry.key, 'title'))}
                    onToggle={() => setOpen(open === index ? -1 : index)}
                    onChange={(next) => changeFeatured(index, next)}
                    onMove={(dir) => moveFeatured(index, dir)}
                    onRemove={() => { setFeatured((list) => list.filter((_, i) => i !== index)); setOpen(-1); }}
                    onUpload={(file) => uploadFeatured(index, file)}
                    onGoHome={() => { setTab(entry.kind); setOpen(at); }}
                  />
                );
              })}
            </ol>
          ) : (
            <p className="rounded-lg border border-dashed border-default-300 p-4 text-sm text-default-500">
              Sin destacados: en la app, «Todo» empieza por las novedades.
            </p>
          )}
          {saveRow()}
        </div>
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-default-500">Así se ve en la app (Galería de overlays → Todo)</p>
          <div className="overflow-hidden rounded-xl border border-default-200 p-5" style={{ background: '#040a12' }}>
            {featured.length ? (
              <>
                <p className="mb-3 text-lg font-bold" style={{ color: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>Destacados</p>
                <div className="grid grid-cols-3 gap-[18px]">
                  {featured.map((entry, index) => (
                    <FeaturedCard
                      key={`${entry.kind}|${entry.key}`} entry={entry} option={featuredOption(entry)} lang={lang} selected={open === index}
                      look={M.presentation(form, entry.kind, entry.key) || { image: '', title: {} }} onSelect={() => setOpen(index)}
                    />
                  ))}
                </div>
              </>
            ) : <p className="py-10 text-center text-sm text-default-500">Añade hasta tres destacados para verlos aquí.</p>}
          </div>
          <p className="text-xs text-default-500">En la app llevan además su etiqueta NEW o «Mejorado» si la tienen, y la imagen de la app se ve entera (la que subas aquí llena la tarjeta).</p>
        </div>
      </div>
      ) : (
      <div className="grid gap-5 xl:grid-cols-[minmax(0,460px)_minmax(0,1fr)]">
        {/* ── Formulario ── */}
        <div className="min-w-0 space-y-3">
          {canWrite ? (
            <button
              type="button" disabled={full} onClick={() => setPicking(true)} data-add-items
              className="flex w-full items-center gap-3 rounded-lg border border-dashed border-primary/60 bg-primary/5 px-3 py-2.5 text-left transition enabled:hover:bg-primary/10 disabled:cursor-not-allowed disabled:border-default-300 disabled:bg-transparent"
            >
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-md text-xl leading-none ${full ? 'bg-default-100 text-default-400' : 'bg-primary/15 text-primary'}`} aria-hidden="true">+</span>
              <span className="min-w-0">
                <span className={`block text-sm font-semibold ${full ? 'text-default-500' : 'text-primary'}`}>
                  {full ? `Máximo ${M.MAX_ITEMS} elementos` : `Añadir a «${section.label}»`}
                </span>
                <span className="block text-xs text-default-500">
                  {full
                    ? 'Quita alguno para añadir otro.'
                    : `Se eligen viendo su imagen · ${free.length} por añadir · ${M.MAX_ITEMS - items.length === 1 ? 'cabe 1' : `caben ${M.MAX_ITEMS - items.length}`} más`}
                </span>
              </span>
            </button>
          ) : null}

          {items.length ? (
            <ol className="space-y-2">
              {items.map((item, index) => (
                <ItemRow
                  key={item.key} section={section} item={item} option={byKey.get(norm(item.key))} index={index} count={items.length}
                  open={open === index} canWrite={canWrite} uploading={uploading === index} stale={itemStale(item)}
                  onToggle={() => setOpen(open === index ? -1 : index)}
                  onChange={(next) => changeItem(index, next)}
                  onMove={(dir) => move(index, dir)}
                  onRemove={() => remove(index)}
                  onUpload={(file) => upload(index, file)}
                />
              ))}
            </ol>
          ) : (
            <p className="rounded-lg border border-dashed border-default-300 p-4 text-sm text-default-500">
              Sin elegir: en la app este carrusel se rellena solo con los más usados por la comunidad.
            </p>
          )}

          {saveRow(items.length ? <Button size="sm" variant="light" onPress={() => { setItems([]); setOpen(-1); }}>Vaciar (automático)</Button> : null)}
        </div>

        {/* ── Así se ve en la app ── */}
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-default-500">Así se ve en la app</p>
          <div className="overflow-hidden rounded-xl border border-default-200 p-5" style={{ background: '#0E0E12' }}>
            {items.length
              ? <Preview section={section} items={items} options={sectionOptions} lang={lang} selected={open} onSelect={setOpen} />
              : <p className="py-10 text-center text-sm text-default-500">Añade elementos para verlos aquí. Mientras esté vacío, la app enseña los más usados.</p>}
          </div>
          <p className="text-xs text-default-500">
            {section.hint} Pasa el ratón por una tarjeta para verlo. En la app el carrusel ocupa todo el ancho
            ({tab === 'animations' ? '7' : '5'} tarjetas a la vez en una ventana normal) y se mueve con sus flechas.
          </p>
          {tab === 'games' && items[open] && validVideos(items[open]).length ? (
            <div className="space-y-2 pt-2" data-game-modal-preview>
              <p className="text-xs font-semibold uppercase tracking-wide text-default-500">Al pulsar su tarjeta</p>
              <div className="overflow-hidden rounded-xl border border-default-200 p-5" style={{ background: 'rgba(4, 6, 14, 0.9)' }}>
                <GameModalPreview item={items[open]} option={byKey.get(norm(items[open].key))} lang={lang} />
              </div>
              <p className="text-xs text-default-500">Cada vídeo se abre en el navegador; «Ir al juego» lleva a su ficha en Gaming. El botón «Jugar» de la tarjeta va directo a la ficha.</p>
            </div>
          ) : null}
        </div>
      </div>
      )}

      {translating ? (
        <TranslateDialog
          pending={toTranslate} everything={ownTexts} promptFor={translationPrompt} targets={ALL_LANGS.length - 1}
          onApply={applyTranslated} onClose={() => setTranslating(false)}
        />
      ) : null}

      {pickingKind ? (
        <Picker
          section={{ id: pickingKind === 'goals' ? 'widgets' : pickingKind, label: `Destacados · ${KIND_LABEL[pickingKind]}` }} options={featuredOptions[pickingKind]}
          used={new Set(featured.filter((it) => it.kind === pickingKind).map((it) => norm(it.key)))} room={M.MAX_GALLERY - featured.length}
          max={M.MAX_GALLERY} where="la fila de destacados" loading={false} onAdd={(keys) => addFeatured(pickingKind, keys)} onClose={() => setPickingKind('')}
        />
      ) : null}

      {picking ? (
        <Picker
          section={section} options={sectionOptions} used={used} room={M.MAX_ITEMS - items.length}
          loading={tab === 'games' && !manifest} onAdd={add} onClose={() => setPicking(false)}
        />
      ) : null}
    </section>
  );
}
